"use client";

import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from "react";
import type { WidgetMessageView } from "../../contracts";
import "./site-chat.css";

// The site widget (website-widget spec) in the design «Пульт»
// (prototype/cabinet/shared/widget.js with pult/widget.css): a launcher and a
// panel with the company's accent colour. The visitor's messages go to the
// widget route with an idempotency key; while the panel is open their latest
// conversation is read every 10 seconds. Text only; the visitor's cookie is
// set by the server and never seen here.

const POLL_MS = 10_000;
const TEXT_MAX = 2_000;

export type SiteChatCopy = {
  launcher: string;
  close: string;
  dialog: string;
  status: string;
  greeting: string;
  placeholder: string;
  label: string;
  send: string;
  poweredBy: string;
  failed: string;
  limited: string;
  attachment: string;
};

export type SiteChatProps = {
  siteKey: string;
  locale: string;
  company: string;
  /** The company's colour; text on it is chosen for contrast. */
  accent: string;
  logoSrc: string | null;
  copy: SiteChatCopy;
};

/** Text colour that stays readable on the accent (WCAG relative luminance). */
function onAccent(hex: string): string {
  const value = hex.replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const n = Number.parseInt(full, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? "#0b1a1d" : "#ffffff";
}

function Icon({ path }: { path: string }) {
  return <svg className="iw-ic" viewBox="0 0 24 24" aria-hidden="true"><path d={path} /></svg>;
}
const CHAT = "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z";
const CLOSE = "M18 6 6 18M6 6l12 12";
const DOWN = "m6 9 6 6 6-6";
const SEND = "M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11zM21.854 2.147l-10.94 10.939";

export function SiteChat({ siteKey, locale, company, accent, logoSrc, copy }: SiteChatProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<WidgetMessageView[]>([]);
  const [sending, setSending] = useState<{ key: string; text: string } | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  // The key of a message that failed: sending the same text again reuses it, so the server stores it once.
  const failed = useRef<{ key: string; text: string } | null>(null);
  const endpoint = `/api/widget/${encodeURIComponent(siteKey)}/messages`;
  const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });

  const load = useCallback(async () => {
    try {
      const response = await fetch(endpoint, { credentials: "same-origin", cache: "no-store" });
      if (!response.ok) return;
      const body = (await response.json()) as { messages?: WidgetMessageView[] };
      if (Array.isArray(body.messages)) setMessages(body.messages);
    } catch {
      // The next poll tries again.
    }
  }, [endpoint]);

  // Opening reads the conversation at once (in the click); the panel then polls.
  function toggle() {
    if (!open) void load();
    setOpen(!open);
  }

  useEffect(() => {
    if (!open) return;
    field.current?.focus();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, load]);

  async function send(event?: FormEvent) {
    event?.preventDefault();
    const value = text.trim();
    if (!value || sending) return;
    const key = failed.current?.text === value ? failed.current.key : crypto.randomUUID();
    setSending({ key, text: value });
    setError(null);
    setText("");
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: value, key, locale }),
      });
      if (response.ok) {
        failed.current = null;
        await load();
        setSending(null);
        return;
      }
      setError(response.status === 429 ? copy.limited : copy.failed);
    } catch {
      setError(copy.failed);
    }
    failed.current = { key, text: value };
    setText(value);
    setSending(null);
  }

  function onKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <div className={`iw${open ? " open" : ""}`} style={{ "--accent": accent, "--on-accent": onAccent(accent) } as CSSProperties} onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}>
      {open ? (
        <section className="iw-panel" role="dialog" aria-label={copy.dialog}>
          <header className="iw-head">
            <span className={`iw-logo${logoSrc ? " img" : ""}`} aria-hidden="true">
              {/* eslint-disable-next-line @next/next/no-img-element -- a small fixed SVG mark */}
              {logoSrc ? <img src={logoSrc} alt="" width={22} height={22} /> : Array.from(company)[0]}
            </span>
            <span className="iw-title"><b>{company}</b><small><i className="iw-on" />{copy.status}</small></span>
            <button className="iw-close" type="button" aria-label={copy.close} onClick={() => setOpen(false)}><Icon path={DOWN} /></button>
          </header>
          <div className="iw-log" aria-live="polite">
            <div className="iw-log-in">
              <div className="iw-m in auto"><p>{copy.greeting}</p></div>
              {messages.map((message) => (
                <div key={message.id} className={`iw-m ${message.from === "visitor" ? "out" : "in"}${message.from === "auto" ? " auto" : ""}`}>
                  {message.from === "company" ? <span className="iw-who">{company}</span> : null}
                  <p>{message.text ?? copy.attachment}</p>
                  <time dateTime={message.at}>{time.format(new Date(message.at))}</time>
                </div>
              ))}
              {sending ? <div className="iw-m out is-sending"><p>{sending.text}</p></div> : null}
            </div>
          </div>
          {error ? <p className="iw-error" role="alert">{error}</p> : null}
          <form className="iw-compose" onSubmit={send}>
            <textarea ref={field} rows={1} value={text} maxLength={TEXT_MAX} placeholder={copy.placeholder} aria-label={copy.label} onChange={(event) => setText(event.target.value)} onKeyDown={onKey} />
            <button className="iw-send" type="submit" aria-label={copy.send} disabled={!text.trim() || sending !== null}><Icon path={SEND} /></button>
          </form>
          <footer className="iw-foot">{copy.poweredBy}</footer>
        </section>
      ) : null}
      <button className="iw-launch" type="button" aria-expanded={open} aria-label={open ? copy.close : copy.launcher} onClick={toggle}>
        <Icon path={open ? CLOSE : CHAT} />
      </button>
    </div>
  );
}
